import { useState, useRef, useEffect, useCallback } from 'react'
import { Card } from '@/components/ui'
import { aiPartnerChat, type PartnerChatMessage, type SavedMemory } from '@/lib/edgeAi'
import { PetAvatar } from '@/components/PetAvatar'
import { usePetName } from '@/hooks/usePetName'
import { supabase } from '@/lib/supabase'
import { recordButtonClick } from '@/lib/usageTelemetry'

interface MessageMeta {
  savedMemories?: SavedMemory[]
  forgottenMemories?: number
}

interface Props {
  /** 呼び出し元の識別（chat_interactions の entry_point に使う） */
  entryPoint?: string
  /** カードの下にさらに表示するチルドレン */
  children?: React.ReactNode
  /** 相棒の自動生成コメント。あれば履歴空のときの placeholder として表示する */
  briefingMessage?: string | null
}

const SESSION_TTL_HOURS = 24  // 24h 以内のセッションを継続候補として復元

function newSessionId(): string {
  return `partner-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`
}

/** 相棒との対話カード。Today ページに常駐。
 *  - collapsed/expanded を撤廃 (textarea 即表示で動線を短く)
 *  - chat_sessions (server-side) から「直近 24h・ended_at NULL」のアクティブセッションを復元
 *  - device 跨ぎ・分析対象としてセッションが永続される (v14: localStorage 撤廃、growth_events 34e660e6 後継)
 *  - 「新しく話す」で旧 session に ended_at をセットし、新 session_id を発行
 */
export function FutureYouChat({ entryPoint = 'today_partner', children, briefingMessage }: Props) {
  const { name: petName } = usePetName()
  const [history, setHistory] = useState<PartnerChatMessage[]>([])
  const [messageMeta, setMessageMeta] = useState<Record<number, MessageMeta>>({})
  const [input, setInput] = useState('')
  const [sending, setSending] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [restoring, setRestoring] = useState(true)
  const sessionIdRef = useRef<string>('')
  const scrollRef = useRef<HTMLDivElement>(null)

  // セッション復元 (mount 時に 1 回)
  useEffect(() => {
    let alive = true
    ;(async () => {
      try {
        const cutoff = new Date(Date.now() - SESSION_TTL_HOURS * 3600 * 1000).toISOString()
        // 直近のアクティブセッション (ended_at NULL かつ TTL 以内) を取得
        const { data: sessions } = await supabase
          .from('chat_sessions')
          .select('id, last_active_at')
          .eq('entry_point', entryPoint)
          .is('ended_at', null)
          .gte('last_active_at', cutoff)
          .order('last_active_at', { ascending: false })
          .limit(1)

        const activeSessionId = sessions && sessions.length > 0 ? (sessions[0] as { id: string }).id : null

        if (activeSessionId) {
          const { data: msgs } = await supabase
            .from('chat_interactions')
            .select('user_message, assistant_message, created_at')
            .eq('session_id', activeSessionId)
            .order('created_at', { ascending: true })
          if (alive && msgs && msgs.length > 0) {
            const restored: PartnerChatMessage[] = []
            for (const row of msgs as { user_message: string; assistant_message: string }[]) {
              if (row.user_message) restored.push({ role: 'user', content: row.user_message })
              if (row.assistant_message) restored.push({ role: 'assistant', content: row.assistant_message })
            }
            sessionIdRef.current = activeSessionId
            setHistory(restored)
            setRestoring(false)
            return
          }
        }
      } catch {
        // fallthrough to new session
      }

      // 新規セッション (chat_sessions には初回 send 時に trigger で自動 INSERT される)
      sessionIdRef.current = newSessionId()
      if (alive) setRestoring(false)
    })()
    return () => { alive = false }
  }, [entryPoint])

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight
    }
  }, [history])

  const send = async () => {
    const text = input.trim()
    if (!text || sending) return
    recordButtonClick('partner:send')
    setError(null)
    setSending(true)
    const userMsg: PartnerChatMessage = { role: 'user', content: text }
    const nextHistory = [...history, userMsg]
    setHistory(nextHistory)
    setInput('')
    try {
      const { content, savedMemories, forgottenMemories } = await aiPartnerChat(text, history, {
        sessionId: sessionIdRef.current,
        entryPoint,
      })
      const nextAll = [...nextHistory, { role: 'assistant' as const, content }]
      setHistory(nextAll)
      if (savedMemories.length > 0 || forgottenMemories > 0) {
        const assistantIdx = nextAll.length - 1
        setMessageMeta((prev) => ({
          ...prev,
          [assistantIdx]: { savedMemories, forgottenMemories },
        }))
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : 'エラーが発生しました')
      setHistory(nextHistory)
    } finally {
      setSending(false)
    }
  }

  const startNew = useCallback(async () => {
    // 旧 session に ended_at をセット (履歴として残す)
    const oldId = sessionIdRef.current
    if (oldId) {
      try {
        await supabase
          .from('chat_sessions')
          .update({ ended_at: new Date().toISOString() })
          .eq('id', oldId)
      } catch {
        // 失敗しても新セッション作成は続行
      }
    }
    sessionIdRef.current = newSessionId()
    setHistory([])
    setMessageMeta({})
    setInput('')
    setError(null)
  }, [])

  const onKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if ((e.metaKey || e.ctrlKey) && e.key === 'Enter') {
      e.preventDefault()
      send()
    }
  }

  return (
    <Card style={{ marginBottom: 16, background: 'var(--accent-bg)', border: '1px solid var(--accent-border)' }}>
      {/* ヘッダー: アバター + 名前 + 履歴 + 「新しく話す」(履歴あるときだけ) */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10 }}>
        <PetAvatar size={32} />
        <div style={{ fontSize: 13, color: 'var(--text)', fontWeight: 500, flex: 1 }}>{petName}</div>
        <a
          href="/partner/history"
          title="過去の対話を見る"
          style={{ fontSize: 11, color: 'var(--text3)', textDecoration: 'none' }}
        >
          履歴
        </a>
        {history.length > 0 && (
          <button
            type="button"
            onClick={startNew}
            title="新しく話す（今までの会話は残ります）"
            style={{
              fontSize: 11,
              color: 'var(--text3)',
              background: 'transparent',
              border: 'none',
              cursor: 'pointer',
              padding: '2px 6px',
              fontFamily: 'inherit',
            }}
          >
            新しく話す
          </button>
        )}
      </div>

      {/* 会話履歴 (あるときだけ) */}
      {history.length > 0 && (
        <div
          ref={scrollRef}
          style={{
            maxHeight: 360,
            overflowY: 'auto',
            display: 'flex',
            flexDirection: 'column',
            gap: 10,
            padding: '4px 2px 12px',
            marginBottom: 10,
            borderBottom: '1px solid var(--border)',
          }}
        >
          {history.map((m, i) => {
            const meta = messageMeta[i]
            return (
              <div
                key={i}
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: m.role === 'user' ? 'flex-end' : 'flex-start',
                  maxWidth: '88%',
                  alignSelf: m.role === 'user' ? 'flex-end' : 'flex-start',
                  gap: 4,
                }}
              >
                <div
                  style={{
                    padding: '9px 13px',
                    borderRadius: 10,
                    background: m.role === 'user' ? 'var(--surface2)' : 'rgba(255,255,255,0.6)',
                    border: m.role === 'assistant' ? '1px solid var(--border)' : 'none',
                    fontSize: 13,
                    color: 'var(--text)',
                    lineHeight: 1.7,
                    whiteSpace: 'pre-wrap',
                  }}
                >
                  {m.content}
                </div>
                {meta && (meta.savedMemories?.length || meta.forgottenMemories) ? (
                  <div
                    title={meta.savedMemories?.map((s) => s.content).join('\n')}
                    style={{
                      fontSize: 10,
                      color: 'var(--text3)',
                      padding: '0 4px',
                      fontStyle: 'italic',
                      cursor: meta.savedMemories?.length ? 'help' : 'default',
                    }}
                  >
                    {meta.savedMemories?.length ? `✓ 覚えておきました（${meta.savedMemories.length}件）` : ''}
                    {meta.forgottenMemories ? `${meta.savedMemories?.length ? ' · ' : ''}忘れました（${meta.forgottenMemories}件）` : ''}
                  </div>
                ) : null}
              </div>
            )
          })}
          {sending && (
            <div style={{ alignSelf: 'flex-start', fontSize: 11, color: 'var(--text3)', padding: '4px 6px' }}>
              考え中...
            </div>
          )}
        </div>
      )}

      {/* 履歴ゼロかつ briefingMessage があれば「呼びかけ」として表示 */}
      {history.length === 0 && briefingMessage && (
        <div
          style={{
            fontSize: 13,
            color: 'var(--text)',
            lineHeight: 1.6,
            padding: '6px 10px 10px',
          }}
        >
          {briefingMessage}
        </div>
      )}

      {/* textarea: 常時表示 (collapsed なし) */}
      <textarea
        className="input"
        placeholder={restoring ? '読み込み中…' : history.length > 0 ? '続けて話す（Cmd/Ctrl+Enterで送信）' : '話したいこと（Cmd/Ctrl+Enterで送信）'}
        value={input}
        onChange={(e) => setInput(e.target.value)}
        onKeyDown={onKeyDown}
        disabled={sending || restoring}
        style={{ width: '100%', boxSizing: 'border-box', minHeight: 56, fontSize: 13, resize: 'vertical' }}
      />

      {error && <div style={{ fontSize: 11, color: 'var(--red)', marginTop: 4 }}>{error}</div>}

      <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 8 }}>
        <button
          className="btn btn-primary btn-sm"
          onClick={send}
          disabled={!input.trim() || sending || restoring}
          style={{ fontSize: 12, padding: '5px 14px' }}
        >
          送信
        </button>
      </div>

      {children}
    </Card>
  )
}
